import React from 'react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import type { BetLoopProfile } from '@core/Areas/BetLoop/types'
import { ProfileCard } from '@core/Areas/BetLoop/Components/Common/ProfileCard'
import { EditMyProfile } from '@core/Areas/BetLoop/Components/EditMyProfile'
import {
  trackBetLoopCloseOnboarding,
  trackBetLoopEditMyProfile,
  trackBetLoopLaunchBetLoop,
} from '@core/Areas/BetLoop/helpers/analytics'

export const ProfileCreationDisplayTray: React.FC<{
  onCompleteOnboarding(): void
  profileData?: BetLoopProfile
}> = ({ onCompleteOnboarding, profileData }) => {
  const [isProfileCreationTrayOpen, setIsProfileCreationTrayOpen] = React.useState(false)
  const [workflowStatus, setWorkflowStatus] = React.useState<OnboardingWorkflowStatus>('welcome')

  React.useEffect(() => {
    if (!!profileData && profileData.id) setIsProfileCreationTrayOpen(true)
  }, [profileData])

  if (!profileData) return null

  const continueToBetLoop = () => {
    setIsProfileCreationTrayOpen(false)
    setTimeout(onCompleteOnboarding, 400)
  }

  const handleEditProfileClick = () => {
    setWorkflowStatus('edit-profile')
    trackBetLoopEditMyProfile('Onboarding')
  }

  const handleTrayDismiss = () => {
    trackBetLoopCloseOnboarding()
    continueToBetLoop()
  }

  const handleLaunchBetLoopClick = () => {
    trackBetLoopLaunchBetLoop()
    continueToBetLoop()
  }

  if (workflowStatus === 'edit-profile') {
    return <EditMyProfile shouldAutoOpenTray onCompleteOnboarding={onCompleteOnboarding} />
  }

  return createPortal(
    <Tray
      isOpen={isProfileCreationTrayOpen}
      headerTitle={`Welcome, ${profileData.displayName}`}
      onDismiss={handleTrayDismiss}
      footerComponent={
        <>
          <Button
            size='md'
            isFullWidth
            color='secondary_grey'
            onClick={handleEditProfileClick}
            data-testid='ProfileCreation.Edit'
          >
            Edit profile
          </Button>

          <Button
            size='md'
            isFullWidth
            color='primary'
            onClick={handleLaunchBetLoopClick}
            data-testid='ProfileCreation.Launch'
          >
            Launch Bet Loop
          </Button>
        </>
      }
    >
      <WelcomeWrapperStyled>
        <div>
          <ProfileCard profile={profileData} />
        </div>

        <p>
          Your <strong>Profile Card</strong> displays your username, avatar, and key stats - it’s
          how others see you in Bet Loop.
        </p>

        <p>
          We’ve set you up with a random username and avatar. Tap <strong>Edit profile</strong> to
          personalise it now, or change it later from your profile.
        </p>
      </WelcomeWrapperStyled>
    </Tray>,
    document.body
  )
}

// ======
// Styles
// ======

const WelcomeWrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.smx1,

  '> div:first-of-type': {
    display: 'flex',
    flexDirection: 'column',
    width: `calc(100% - (${spacing.sm} * 2))`,
    maxWidth: '35rem',
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },

  '> p': {
    alignSelf: 'stretch',
    margin: 0,
    fontFamily: font.family.primary,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    fontWeight: font.weight.regular,
    color: colors.neutral[800],
    textAlign: 'unset',
    textWrap: 'pretty',

    '> strong': { fontWeight: font.weight.bold },
  },
})

// =====
// Types
// =====

type OnboardingWorkflowStatus = 'welcome' | 'edit-profile'
